import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  AdminRowActionsMenu,
  buildActionsColumn,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import { createAlumni, fetchAlumni, updateAlumni } from "../../../lib/api";

function emptyForm() {
  return { description: "" };
}

function rowIsActive(row) {
  return row.active !== false;
}

function truncate(text, max = 120) {
  const t = String(text ?? "").trim();
  if (!t) return "—";
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

const ALUMNI_COLUMNS = [
  {
    key: "description",
    label: "Description",
    width: "60%",
    tdClassName: "adm-data-cell-muted adm-msg-cell",
    render: (r) => truncate(r.description, 140),
  },
  {
    key: "status",
    label: "Status",
    width: "100px",
    render: (r) => (
      <span className={`adm-status-pill${rowIsActive(r) ? " adm-status-pill--active" : " adm-status-pill--inactive"}`}>
        {rowIsActive(r) ? "Active" : "Inactive"}
      </span>
    ),
  },
];

export default function AdminCustomAlumni() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm());
  const [drawerMode, setDrawerMode] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const rowActions = useAdminRowActionsMenu();

  const reload = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const isActiveParam = filter === "active" ? true : filter === "inactive" ? false : undefined;
      const [allRes, filteredRes] = await Promise.all([
        fetchAlumni(),
        isActiveParam === undefined ? fetchAlumni() : fetchAlumni(isActiveParam),
      ]);
      setAllRecords(allRes.data || []);
      setRecords(isActiveParam === undefined ? allRes.data || [] : filteredRes.data || []);
    } catch (err) {
      setListError(err.message || "Could not load alumni reviews.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const cancelAdd = useCallback(() => {
    setAddOpen(false);
    setAddForm(emptyForm());
    setFormError("");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setEditingRow(null);
    setEditForm(emptyForm());
    setFormError("");
    rowActions.setActionMenu(null);
  }, [rowActions]);

  const openView = (row) => {
    setEditingRow(row);
    setEditForm({ description: row.description ?? "" });
    setDrawerMode("view");
  };

  const openUpdate = (row) => {
    setEditingRow(row);
    setEditForm({ description: row.description ?? "" });
    setFormError("");
    setDrawerMode("edit");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingRow) return;
    const description = String(editForm.description).trim();
    if (!description) return;

    setSaving(true);
    setFormError("");
    try {
      await updateAlumni(editingRow.id, { description });
      closeDrawer();
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not save review.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const description = String(addForm.description).trim();
    if (!description) return;

    setSaving(true);
    setFormError("");
    try {
      await createAlumni({ description });
      setAddForm(emptyForm());
      setAddOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not create review.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const setAlumniActive = async (row, active) => {
    try {
      await updateAlumni(row.id, { active });
      await reload();
    } catch (err) {
      setListError(err.message || "Could not update status.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    }
  };

  useEffect(() => {
    if (!addOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") cancelAdd();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [addOpen, cancelAdd]);

  useEffect(() => {
    if (!drawerMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerMode, closeDrawer]);

  const counts = useMemo(() => {
    const active = allRecords.filter((r) => rowIsActive(r)).length;
    return { all: allRecords.length, active, inactive: allRecords.length - active };
  }, [allRecords]);

  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  }, [records]);

  const tableColumns = useMemo(() => {
    const mapped = ALUMNI_COLUMNS.map((c) => ({
      id: c.key,
      header: c.label,
      width: c.width,
      tdClassName: c.tdClassName,
      render: (row) => (c.render ? c.render(row) : String(row[c.key] ?? "—")),
    }));
    return [
      ...mapped,
      buildActionsColumn({
        rowActions,
        getRowLabel: () => "review",
      }),
    ];
  }, [rowActions]);

  const renderDescriptionField = (form, setForm, isView, autoFocus = false) => (
    <label className="adm-field">
      <span className="adm-field-label">
        Description<span className="adm-req"> *</span>
      </span>
      <p className="adm-upload-hint">
        Alumni review text shown on the Contact page carousel. You can include the name on a new line at the end.
      </p>
      <textarea
        className="adm-input adm-input--textarea"
        rows={6}
        value={form.description}
        onChange={(e) => {
          setForm({ description: e.target.value });
          setFormError("");
        }}
        placeholder="Attending Shristi was an incredible experience…"
        autoFocus={autoFocus}
        readOnly={isView}
        disabled={isView}
      />
    </label>
  );

  return (
    <div className="adm-page adm-page--wide">
      <div className="adm-embedded-simple-crud">
        <header className="adm-page-head adm-page-head--embedded adm-page-head--row">
          <h2 className="adm-page-title adm-page-title--embedded">Custom alumni</h2>
          <button type="button" className="adm-btn adm-btn--primary" onClick={() => setAddOpen(true)} disabled={addOpen}>
            Add review
          </button>
        </header>

        {listError ? (
          <p className="adm-field-error" role="alert">
            {listError}
          </p>
        ) : null}

        <div className="adm-banner-toolbar adm-banner-toolbar--filters-only">
          <div className="adm-banner-toolbar-left" role="tablist" aria-label="Filter alumni reviews by status">
            <button
              type="button"
              role="tab"
              aria-selected={filter === "all"}
              className={`adm-filter-tab${filter === "all" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All ({counts.all})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "active"}
              className={`adm-filter-tab${filter === "active" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("active")}
            >
              Active ({counts.active})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "inactive"}
              className={`adm-filter-tab${filter === "inactive" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("inactive")}
            >
              Inactive ({counts.inactive})
            </button>
          </div>
        </div>

        {addOpen &&
          createPortal(
            <div className="adm-drawer-root">
              <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={cancelAdd} />
              <aside className="adm-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="adm-alumni-drawer-title">
                <div className="adm-drawer-panel-inner">
                  <header className="adm-drawer-header">
                    <h2 id="adm-alumni-drawer-title" className="adm-drawer-title">
                      Add review
                    </h2>
                    <button type="button" className="adm-drawer-close" onClick={cancelAdd} aria-label="Close">
                      <span aria-hidden>×</span>
                    </button>
                  </header>
                  <form className="adm-drawer-form" onSubmit={handleAdd}>
                    <div className="adm-drawer-fields">
                      {renderDescriptionField(addForm, setAddForm, false, true)}
                      {formError ? (
                        <p className="adm-field-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                    </div>
                    <footer className="adm-drawer-footer">
                      <button type="button" className="adm-btn adm-btn--ghost" onClick={cancelAdd}>
                        Cancel
                      </button>
                      <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </footer>
                  </form>
                </div>
              </aside>
            </div>,
            document.body
          )}

        {(drawerMode === "view" || drawerMode === "edit") &&
          createPortal(
            <div className="adm-drawer-root">
              <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={closeDrawer} />
              <aside className="adm-drawer-panel" role="dialog" aria-modal="true">
                <div className="adm-drawer-panel-inner">
                  <header className="adm-drawer-header">
                    <h2 className="adm-drawer-title">
                      {drawerMode === "view" ? "View review" : "Update review"}
                    </h2>
                    <button type="button" className="adm-drawer-close" onClick={closeDrawer} aria-label="Close">
                      <span aria-hidden>×</span>
                    </button>
                  </header>
                  <form className="adm-drawer-form" onSubmit={saveEdit}>
                    <div className="adm-drawer-fields">
                      {renderDescriptionField(editForm, setEditForm, drawerMode === "view")}
                      {formError ? (
                        <p className="adm-field-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                    </div>
                    <footer className="adm-drawer-footer">
                      <button type="button" className="adm-btn adm-btn--ghost" onClick={closeDrawer}>
                        {drawerMode === "view" ? "Close" : "Cancel"}
                      </button>
                      {drawerMode === "edit" ? (
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                          {saving ? "Saving…" : "Save changes"}
                        </button>
                      ) : null}
                    </footer>
                  </form>
                </div>
              </aside>
            </div>,
            document.body
          )}

        <AdminRowActionsMenu
          actionMenu={rowActions.actionMenu}
          onClose={rowActions.close}
          ariaLabel="Alumni review actions"
          isActive={rowActions.actionMenu ? rowIsActive(rowActions.actionMenu.row) : false}
          onView={(row) => openView(row)}
          onUpdate={(row) => openUpdate(row)}
          onToggleActive={(row) => setAlumniActive(row, !rowIsActive(row))}
        />

        <section className="adm-card adm-card--table">
          {loading ? (
            <p className="adm-empty">Loading…</p>
          ) : (
            <AdminDataTable
              layout="fixed"
              columns={tableColumns}
              rows={sortedRecords}
              getRowKey={(r) => r.id}
              emptyState={
                <span className="adm-empty adm-empty--in-table">
                  {allRecords.length === 0
                    ? "No alumni reviews yet."
                    : "No reviews match this filter."}
                </span>
              }
            />
          )}
        </section>
      </div>
    </div>
  );
}
